import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import {
  GoogleMap,
  MarkerClusterer,
  Marker,
  InfoWindow,
  Polygon,
} from "@react-google-maps/api";

import { getMapIcon, fbpolygon } from "../helpers";
import { breakpoints } from "../ui-components";

import MAPSTYLE from "../static/map-style.json";

const markerClusterStyles = [
  {
    width: 32,
    height: 32,
    className: "clustericon-1",
    textColor: "#FFFFFF",
    textSize: 16,
    fontFamily: "Nunito, sans-serif",
    fontWeight: 800,
    url: "https://mappie.io/build/images/fb-cluster-bg-sm.svg",
  },
  {
    width: 32,
    height: 32,
    className: "clustericon-2",
    textColor: "#FFFFFF",
    textSize: 16,
    fontFamily: "Nunito, sans-serif",
    fontWeight: 800,
    url: "https://mappie.io/build/images/fb-cluster-bg-sm.svg",
  },
  {
    width: 50,
    height: 50,
    className: "clustericon-3",
    textColor: "#FFFFFF",
    textSize: 18,
    fontFamily: "Nunito, sans-serif",
    fontWeight: 800,
    url: "https://mappie.io/build/images/fb-cluster-bg-lg.svg",
  },
];

const polygonOptions = {
  fillOpacity: 0,
  strokeColor: "#ee6731",
  strokeOpacity: 1,
  strokeWeight: 1,
  clickable: false,
  draggable: false,
  editable: false,
  geodesic: false,
  zIndex: 1,
};

const MapContainer = (props) => {
  const { places, isReady, onMarkerClick, userCoords, hoveredCard } = props;

  const [mapRef, setMapRef] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [clusterers, setClusterers] = useState([]);
  const [markerMap, setMarkerMap] = useState({});
  const [placesList, setPlacesList] = useState([]);
  const [hoveredPlace, setHoveredPlace] = useState(null);

  /**
   * Lifecycle method to fit the map to the bounds of all the places when
   * all data has been fetched from the server
   */
  useEffect(() => {
    if (isReady && places.length > 0) fitBounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, places]);

  /**
   * Lifecycle method to update placesList with the list of places fetched from the server
   */
  useEffect(() => {
    setPlacesList(places);
  }, [places]);

  /**
   * Lifecycle method to add hover animation to a cluster when mouse is over a sidebar card
   */
  useEffect(() => {
    if (!clusterers || clusterers.length < 1) return;

    if (!hoveredCard) {
      clusterers.clusters.forEach((cluster) => {
        const icon = cluster.clusterIcon.div;
        if (icon && icon.classList) icon.classList.remove("bounce");
      });

      return;
    }

    let cluster = clusterers.clusters.find((cluster) =>
      cluster.markers.find((marker) => marker.id === hoveredCard)
    );

    if (cluster != null && cluster.markers.length > 1) {
      const icon = cluster.clusterIcon.div;
      if (icon && icon.classList) icon.classList.add("bounce");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredCard]);

  /**
   * Align map to fit all markers
   *
   * @param {object} map
   * @function fitBounds
   */
  const fitBounds = () => {
    const bounds = new window.google.maps.LatLngBounds();
    if (!bounds) return;

    places.forEach((place) =>
      bounds.extend({
        lat: place?.coordinates.latitude,
        lng: place?.coordinates.longitude,
      })
    );
    if (userCoords)
      bounds.extend({ lat: userCoords.latitude, lng: userCoords.longitude });

    const options =
      (window.innerWidth || document.body.clientWidth) >=
      Number(breakpoints.desktop_sm.replace("px", ""))
        ? { left: 750 }
        : {};
    mapRef.fitBounds(bounds, options);
  };

  /**
   * Create a mapping of our places to actual Marker objects
   *
   * @param {object} marker
   * @param {object} place
   * @function markerLoadHandler
   */
  const markerLoadHandler = (marker, place) => {
    setMarkers((prevState) => [...prevState, marker]);

    return setMarkerMap((prevState) => {
      return { ...prevState, [place.place_id]: marker };
    });
  };

  /**
   * On marker click, pass the place to the parent component
   *
   * @param {object} event
   * @param {object} place
   * @function markerClickHandler
   */
  const markerClickHandler = (event, place) => {
    onMarkerClick(place);
  };

  /**
   * Get a list of visible markers and pass the list to the home component.
   * This function is called each time the map becomes idle.
   */
  const getVisibleMarkers = () => {
    if (!markers || !placesList) return;

    let list = placesList.map((place) => {
      return { placeVisibility: false, ...place };
    });
    let destinations = [];

    markers.forEach((marker) => {
      list.forEach((item, index) => {
        if (marker.visible === true && item.place_id === marker.id) {
          list[index].placeVisibility = true;
          destinations.push({
            lat: item?.coordinates.latitude,
            lng: item?.coordinates.longitude,
          });
        }
      });
    });
  };

  /**
   * Update the clusters if the marker visibility has changed for any markers
   *
   * @param {object} clusterer
   * @function visibilityChanged
   */
  const visibilityChanged = (clusterer) => {
    clusterer.repaint();
    getVisibleMarkers();
  };

  /**
   * Returns an icon symbol
   *
   * @param {string} category
   * @function getIconType
   */
  const getIconType = (category) => {
    const url = getMapIcon(category);
    return { url, scaledSize: new window.google.maps.Size(35, 35) };
  };

  return (
    <GoogleMap
      zoom={12}
      mapContainerStyle={{
        height: "100%",
        position: "absolute",
        top: "0",
        left: "0",
        width: `100%`,
      }}
      options={{
        styles: MAPSTYLE,
        mapTypeControl: false,
        streetViewControl: false,
      }}
      onLoad={(map) => setMapRef(map)}
      onIdle={getVisibleMarkers}
      id={"MappieMap"}
    >
      {isReady && (
        <>
          <MarkerClusterer
            minimumClusterSize={4}
            ignoreHidden={true}
            zoomOnClick={true}
            styles={markerClusterStyles}
            clusterClass="clustericon"
            onClusteringEnd={(list) => setClusterers(list)}
          >
            {(clusterer) =>
              places.map((place) => (
                <Marker
                  key={place?.place_id}
                  position={{
                    lat: place?.coordinates.latitude,
                    lng: place?.coordinates.longitude,
                  }}
                  animation={
                    hoveredCard === place.place_id
                      ? window.google.maps.Animation.BOUNCE
                      : null
                  }
                  clusterer={clusterer}
                  onLoad={(marker) => markerLoadHandler(marker, place)}
                  onClick={(event) => markerClickHandler(event, place)}
                  options={{
                    id: place.place_id,
                    category: place.category,
                  }}
                  icon={getIconType(place.category)}
                  defaultVisible={true}
                  onVisibleChanged={() => visibilityChanged(clusterer)}
                  onMouseOver={() => setHoveredPlace(place)}
                  onMouseOut={() => setHoveredPlace(null)}
                />
              ))
            }
          </MarkerClusterer>
          {hoveredPlace && (
            <InfoWindow anchor={markerMap[hoveredPlace.place_id]}>
              <InfoWindowText>{hoveredPlace.name}</InfoWindowText>
            </InfoWindow>
          )}
        </>
      )}
      {userCoords && (
        <Marker
          position={{ lat: userCoords.latitude, lng: userCoords.longitude }}
          icon={{
            url: "https://mappie.io/build/images/pin-around-me.png",
            scaledSize: new window.google.maps.Size(35, 35),
          }}
        />
      )}
      <Polygon paths={fbpolygon()} options={polygonOptions} />
    </GoogleMap>
  );
};

export { MapContainer };

const InfoWindowText = styled.p`
  padding: 0;
  margin: 0;
  font-weight: 600;
  text-transform: capitalize;
`;
